import React, { useEffect, useState } from "react";
import { ApolloResult, Clinic, PSAccount, PSAccountType, PSTransaction } from "../../store";
import withClinic from "../../hooks/with_clinic";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  getAccountTypeVar,
  getAccountsVariables,
  getTotalCountAccountVar,
  getTrxVar,
  get_account_type,
  get_accounts,
  get_total_account_count,
  get_trxs,
} from "../../graphql/pass";
import { get_clinic_members, getClinicMembersVar } from "../../graphql/clinic_member";
import { Card, Row, Col, Typography, Table, Empty, Input } from "antd";
import Cookies from "js-cookie";
import { getPassApiToken } from "../../api/pass_api";
import LoadingSpinner from "../../components/loading_indicator";
import Error from "../../components/apollo_error";
import { formatMoney, formatPhoneNumber } from "../../helpers/utils";
import { HistoryOutlined, HolderOutlined, MinusCircleTwoTone, PlusCircleTwoTone, FileExcelFilled } from "@ant-design/icons";
import { PaginationConfig } from "antd/lib/pagination";
import { format } from "date-fns";
import { ExportExcel } from "../../helpers/excel";
import client from "../../graphql/apollo_client";
import { Button } from "antd";
const { Search } = Input;
interface P {
  currentClinic?: Clinic;
}

function PassAccounts(props: P) {
  const passConfig = () => {
    let config: {
      refresh_token_url?: string;
      refresh_token?: string;
      id?: string;
    } | null = null;
    if (props.currentClinic && props.currentClinic.pass) {
      const obj = JSON.parse(props.currentClinic.pass) as {
        refresh_token_url?: string;
        refresh_token?: string;
        id?: string;
      };
      if (obj.refresh_token && obj.refresh_token_url) {
        config = obj;
      }
    }
    return config;
  };
  const [searchText, setSearchText] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [loading2, setLoading2] = useState(false);
  const [trxAccount, setTrxAccount] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [exporting, setExporting] = useState(false);
  const accountTypeCode = passConfig()?.id || "";
  const currentAccResult = useQuery<
    ApolloResult<"aggregateAccount", { _count: { id: number } }>
  >(get_total_account_count, {
    variables: getTotalCountAccountVar({ code: accountTypeCode, searchText }),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    context: { className: "pass" },
  });

  const [getAccounts, { data, loading, error, refetch }] = useLazyQuery<
    ApolloResult<"accounts", PSAccount[]>
  >(get_accounts, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    context: {
      className: "pass",
    },
  });

  const [getTrxs, { loading: trxLoading, data: trxData }] = useLazyQuery<ApolloResult<"transactions", PSTransaction[]>>(get_trxs, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    context: {
      className: "pass",
    },
  });

  const accounts = data?.accounts ?? [];
  const phoneNumbersWithoutName = accounts
    .filter((a) => !a.customer?.name?.trim())
    .map((a) => a.customer.phone_number)
    .filter(Boolean);
  const { data: clinicMembersData } = useQuery<{ clinicMembers: Array<{ id: string; name: string | null; phonenumber: string; member: { id: string; name: string | null } }> }>(
    get_clinic_members,
    {
      variables: getClinicMembersVar(props.currentClinic?.id ?? "", phoneNumbersWithoutName),
      skip: !props.currentClinic?.id || phoneNumbersWithoutName.length === 0,
    }
  );
  const nameByPhone: Record<string, string> = {};
  (clinicMembersData?.clinicMembers ?? []).forEach((cm) => {
    const displayName = cm.name ?? cm.member?.name ?? "";
    if (displayName) nameByPhone[cm.phonenumber] = displayName;
  });

  const getAccountDisplayName = (name: string | null | undefined, phoneNumber: string) =>
    (name?.trim() || nameByPhone[phoneNumber]) ?? "—";

  useEffect(() => {
    if (trxAccount) {
      getTrxs({
        variables: getTrxVar({
          accountId: trxAccount,
          take: 10,
          skip: 0
        })
      })
    }
  }, [trxAccount])

  useEffect(() => {
    if (error) {
      const pass = passConfig();
      if (
        !Cookies.get("p_token") &&
        pass?.refresh_token &&
        pass?.refresh_token_url
      ) {
        setLoading2(true);
        getPassApiToken(pass.refresh_token_url, pass.refresh_token).then(
          (r) => {
            refetch().then(() => {
              setLoading2(false);
            });
          }
        );
      }
    }
  }, [error]);

  useEffect(() => {
    const variables = getAccountsVariables({
      accountTypeCode,
      searchText,
      take: pageSize,
    });
    getAccounts({ variables });
  }, [searchText, props.currentClinic])

  if (loading || loading2) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!searchText && !data) return <Empty description="no accounts" />;
  const columns: any[] = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      align: "center",
      render: (_: number, r: PSAccount) =>
        getAccountDisplayName(r.customer?.name, r.customer?.phone_number),
    },
    {
      title: "PHONE NUMBER",
      dataIndex: "phone_number",
      key: "phone_number",
      render: (_: number, r: PSAccount) =>
        formatPhoneNumber(r.customer.phone_number, props.currentClinic),
    },
    {
      title: "BALANCE",
      dataIndex: "balance",
      key: "balance",
      sorter: (a: PSAccount, b: PSAccount) =>
        Number(a.balance) - Number(b.balance),
      defaultSortOrder: "descend",
      render: (_: number, r: PSAccount) => formatMoney(Number(r.balance)),
    },
    {
      title: "TRANSACTION COUNT",
      dataIndex: "transaction",
      key: "transaction",
      render: (_: number, r: PSAccount) => (
        <Typography style={{ textAlign: "left" }}>{r._count.transactions}</Typography>
      ),
    },
    Table.EXPAND_COLUMN,
  ];
  const total = currentAccResult.data?.aggregateAccount._count.id || 0;

  const expandedRowRender = (r: PSAccount) => {
    const columns2: any[] = [
      {
        title: "Date",
        dataIndex: "created_at",
        key: "created_at",
        editable: false,
        render: (v: PSTransaction, t: PSTransaction) => {
          return format(new Date(t.created_at), "dd MMM,yyyy h:mm a");
        },
      },
      {
        title: "TransactionNo",
        dataIndex: "transaction_number",
        key: "transaction_number",
        align: "center",
      },
      {
        title: "Balance",
        dataIndex: "balance",
        key: "balance",
        render: (v: number, t: PSTransaction) => {
          return formatMoney(Number(t.balance));
        },
      },
      {
        title: "Status",
        dataIndex: "transaction_status",
        key: "transaction_status",
      },
      {
        title: "Transfer",
        dataIndex: "transfer",
        key: "transfer",
        render: (v: number, t: PSTransaction) => {
          const recipientName = getAccountDisplayName(
            t.transaction_detail.recipient.customer?.name,
            t.transaction_detail.recipient.customer?.phone_number
          );
          const senderName = getAccountDisplayName(
            t.transaction_detail.sender.customer?.name,
            t.transaction_detail.sender.customer?.phone_number
          );
          const text = t.transaction_detail.sender.id === r.id
            ? `Transfer to ${recipientName}`
            : `Transfer from ${senderName}`;
          return <Typography style={{ textAlign: "center" }}>{text}</Typography>;
        },
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
      },
    ];

    return <Table
      size="small"
      loading={trxLoading}
      columns={columns2}
      dataSource={(trxData?.transactions || []).filter(t => t.account_id === r.id)}
      onChange={(p: PaginationConfig) => {
        const take = p.pageSize || 10;
        const skip = ((p.current || 1) - 1) * take;
        getTrxs({
          variables: getTrxVar({
            accountId: trxAccount!,
            skip,
            take,
          })
        })
      }}
      pagination={{
        total: r._count.transactions,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30']
      }}
    />
  }
  const onExpandedRowsChange = async (Ids: string[]) => {
    if (Ids.length > 0) {
      setTrxAccount(Ids[0]);
    } else {
      setTrxAccount(null);
    }
  };

  const handleExport = async () => {
    const fileName = "wallet_accounts_" + format(new Date(), "yyyy-MMM-dd");
    let excelData: any[] = [];

    const header = [
      "Name",
      "Phone Number",
      "Balance",
      "Status",
      "Transaction Count",
    ];

    setExporting(true);
    try {
      let list: PSAccount[] = [];
      let rolling = true;
      const take = 100;
      let skip = 0;

      // Fetch all accounts in batches
      while (rolling) {
        const variables = getAccountsVariables({
          accountTypeCode,
          searchText,
          take,
          skip,
        });
        const result = await client.query<ApolloResult<"accounts", PSAccount[]>>({
          query: get_accounts,
          variables,
          context: { className: "pass" },
        });
        const accounts = result.data?.accounts || [];
        list = list.concat(accounts);
        skip += accounts.length;
        rolling = accounts.length === take;
      }

      // Resolve names from clinic members for export (accounts without name)
      const exportPhoneNumbersWithoutName = list
        .filter((a) => !a.customer?.name?.trim())
        .map((a) => a.customer.phone_number)
        .filter(Boolean);
      const exportMembersResult = exportPhoneNumbersWithoutName.length > 0 && props.currentClinic?.id
        ? await client.query<{ clinicMembers: Array<{ name: string | null; phonenumber: string; member: { name: string | null } }> }>({
            query: get_clinic_members,
            variables: getClinicMembersVar(props.currentClinic.id, exportPhoneNumbersWithoutName),
          })
        : null;
      const exportNameByPhone: Record<string, string> = {};
      (exportMembersResult?.data?.clinicMembers ?? []).forEach((cm) => {
        const displayName = cm.name ?? cm.member?.name ?? "";
        if (displayName) exportNameByPhone[cm.phonenumber] = displayName;
      });

      // Format data for Excel
      for (const account of list) {
        const name = (account.customer?.name?.trim() || exportNameByPhone[account.customer?.phone_number]) ?? "";
        const phoneNumber = formatPhoneNumber(account.customer.phone_number, props.currentClinic);
        const balance = Number(account.balance);
        const status = account.lock ? "locked" : "active";
        const transactionCount = account._count.transactions;

        excelData.push({
          name,
          phone_number: phoneNumber,
          balance,
          status,
          transaction_count: transactionCount,
        });
      }

      ExportExcel(excelData, fileName, header);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col flex={4}>
          <Row>
            <Col span={8}>
              <Typography level={2}>Wallets</Typography>
            </Col>
            <Col span={8} offset={8}>
              <Search
                defaultValue={searchText}
                placeholder={`search ${total} account(s)...`}
                allowClear
                size="default"
                onSearch={(val: any) => setSearchText(val.toLowerCase())}
              />
            </Col>
          </Row>
        </Col>
        <Col flex={0} style={{ marginLeft: 8 }}>
          <Button
            size="middle"
            loading={exporting}
            icon={<FileExcelFilled />}
            onClick={() => handleExport()}
          >
            Export
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: 8 }}>
        <Col span={24}>
          <Table
            loading={loading}
            showHeader={data?.accounts || [].length > 0}
            dataSource={(data?.accounts || []).map((c, i) => ({
              ...c,
              key: c.id,
            }))}
            columns={columns}
            rowClassName="editable-row"
            expandable={{
              expandedRowRender,
              onExpandedRowsChange,
              expandIcon: ({ expanded, onExpand, record }: any) =>
                expanded ? (
                  <MinusCircleTwoTone onClick={e => onExpand(record, e)} />
                ) : (
                  <HistoryOutlined onClick={e => trxAccount ? {} : onExpand(record, e)} style={{ color: trxAccount ? "whitesmoke" : "black" }} />
                )
            }}
            onChange={(p: PaginationConfig) => {
              setPagination(p)
              const take = p.pageSize || pageSize;
              const skip = ((p.current || 1) - 1) * take;
              getAccounts({
                variables: getAccountsVariables({
                  accountTypeCode,
                  searchText,
                  take,
                  skip
                })
              })
            }}
            pagination={{
              total,
              current: pagination?.current,
              pageSize: pagination?.pageSize,
              defaultPageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '30', '50']
            }}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
}

export default withClinic(PassAccounts);
